import { useState, useCallback } from 'react'

import { useAspirantsApi } from '@/services/api/aspirantsApi'
import { useResourcesApi } from '@/services/api/resourcesApi'
import { SapOptions } from '@/types'
import {
    IcfesType,
    IcfesFormField,
    AspirantIcfesExam,
    AspirantIcfesFormPayload,
    IcfesAuditEntry
} from '@/types/icfes'

interface UseIcfesReturn {
    icfesTypes: IcfesType[]
    icfesFormFields: IcfesFormField[]
    foreignExams: SapOptions[]
    loading: boolean
    error: string | null
    getIcfesTypes: () => Promise<void>
    getIcfesFormFields: (tipoIcfes: string) => Promise<void>
    getForeignExams: () => Promise<void>
    getAspirantIcfes: (aspirantId: number) => Promise<AspirantIcfesExam[]>
    getIcfesAudit: (
        aspirantId: number,
        examId: number
    ) => Promise<IcfesAuditEntry[]>
    postIcfesForm: (
        aspirantId: number,
        payload: AspirantIcfesFormPayload,
        agent: number
    ) => Promise<boolean>
}

export const useIcfes = (): UseIcfesReturn => {
    const [icfesTypes, setIcfesTypes] = useState<IcfesType[]>([])
    const [icfesFormFields, setIcfesFormFields] = useState<IcfesFormField[]>([])
    const [foreignExams, setForeignExams] = useState<SapOptions[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const resourcesApi = useResourcesApi()
    const aspirantsApi = useAspirantsApi()

    const getIcfesTypes = async (): Promise<void> => {
        try {
            setLoading(true)
            setError(null)
            const response = await resourcesApi.getIcfesTypes()

            if (response && response.data) {
                setIcfesTypes(response.data)
            } else {
                setError('Error al obtener los tipos de ICFES')
            }
        } catch (err) {
            setError('Error al obtener los tipos de ICFES')
            console.error('Error fetching ICFES types:', err)
        } finally {
            setLoading(false)
        }
    }

    const getIcfesFormFields = async (tipoIcfes: string): Promise<void> => {
        try {
            setLoading(true)
            setError(null)
            const response = await resourcesApi.getIcfesFormFields(tipoIcfes)

            if (response && response.data) {
                setIcfesFormFields(response.data)
            } else {
                setError('Error al obtener los campos del formulario ICFES')
            }
        } catch (err) {
            setError('Error al obtener los campos del formulario ICFES')
            console.error('Error fetching ICFES form fields:', err)
        } finally {
            setLoading(false)
        }
    }

    const getForeignExams = useCallback(async (): Promise<void> => {
        // Evitar llamadas múltiples
        if (loading) {
            console.log('Ya está cargando, saltando...')

            return
        }

        try {
            console.log('Iniciando carga de exámenes extranjeros...')
            setLoading(true)
            setError(null)
            const response = await resourcesApi.getForeingExamList()

            console.log('Respuesta completa:', response)

            // El response ya es el array directamente, no response.data
            if (response && Array.isArray(response)) {
                console.log('Datos recibidos:', response)
                console.log('Tipo de datos:', typeof response)
                console.log('Es array:', Array.isArray(response))
                console.log('Primer elemento:', response[0])

                // Filtrar elementos vacíos y mapear si es necesario
                const processedData = response
                    .filter(
                        (item: any) =>
                            item.value !== '000' &&
                            item.label &&
                            item.label.trim() !== ''
                    )
                    .map((item: any) => ({
                        value: item.value,
                        label: item.label
                    }))

                console.log('Datos procesados:', processedData)
                setForeignExams(processedData)
            } else {
                console.error('No hay datos en la respuesta o no es un array')
                setError('Error al obtener la lista de exámenes extranjeros')
            }
        } catch (err) {
            console.error('Error completo:', err)
            setError('Error al obtener la lista de exámenes extranjeros')
        } finally {
            setLoading(false)
        }
    }, [resourcesApi])

    return {
        icfesTypes,
        icfesFormFields,
        foreignExams,
        loading,
        error,
        getIcfesTypes,
        getIcfesFormFields,
        getForeignExams,
        getAspirantIcfes: async (
            aspirantId: number
        ): Promise<AspirantIcfesExam[]> => {
            try {
                const resp = await aspirantsApi.getIcfesExams(aspirantId)

                return resp.data || []
            } catch (e) {
                console.error('Error getting icfes exams', e)

                return []
            }
        },
        getIcfesAudit: async (
            aspirantId: number,
            examId: number
        ): Promise<IcfesAuditEntry[]> => {
            try {
                const resp = await aspirantsApi.getIcfesAudit(
                    aspirantId,
                    examId
                )

                return resp.data || []
            } catch (e) {
                console.error('Error getting icfes audit', e)

                return []
            }
        },
        postIcfesForm: async (
            aspirantId: number,
            payload: AspirantIcfesFormPayload,
            agent: number
        ): Promise<boolean> => {
            try {
                await aspirantsApi.postIcfesForm(aspirantId, payload, agent)

                return true
            } catch (e) {
                console.error('Error posting icfes form', e)

                return false
            }
        }
    }
}
